import { ALL_FACTORS, FACTORS, pretty, scorePair } from "./graphModel";

const pct = (v) => Math.round(v * 100);

const Tags = ({ items, variant = "neutral", empty }) => (
  <div className="ludograph-tags">
    {items.map((t) => (
      <span key={t} className={`tag tag-${variant}`}>
        {t}
      </span>
    ))}
    {!items.length && empty && <span className="ludograph-small-text">{empty}</span>}
  </div>
);

const ContributionBar = ({ contrib, height = 6 }) => (
  <span className="ludograph-bar" style={{ height }}>
    {ALL_FACTORS.map((f) => (
      <span key={f.key} style={{ width: `${contrib[f.key] * 100}%`, background: f.color }} />
    ))}
  </span>
);

const Legend = () => (
  <div className="ludograph-legend">
    {ALL_FACTORS.map((f) => (
      <span key={f.key}>
        <span className="ludograph-swatch" style={{ background: f.color }} />
        {f.label}
      </span>
    ))}
  </div>
);

// Games ranked by total link weight, with a bar relative to the whole graph.
const StrengthList = ({ indices, graph, games, onSelect, onEnter, onLeave }) => {
  const maxStrength = Math.max(...graph.strength) || 1;
  return indices.map((i) => (
    <button
      key={games[i].id}
      className="ludograph-top"
      onClick={() => onSelect(i)}
      onMouseEnter={() => onEnter(i)}
      onMouseLeave={onLeave}
    >
      <span className="ludograph-top-name">{games[i].name}</span>
      <span className="ludograph-num ludograph-small-text">{graph.adj[i].length} links</span>
      <span className="ludograph-bar ludograph-top-bar">
        <span
          style={{
            width: `${(graph.strength[i] / maxStrength) * 100}%`,
            background: "var(--color-text)",
          }}
        />
      </span>
    </button>
  ));
};

const EmptyView = (props) => (
  <>
    <section className="ludograph-section">
      <h3>Select a game</h3>
      <p className="ludograph-muted">
        Click any square to see what links it to the rest of your shelf, or pick a cluster on the
        left.
      </p>
    </section>
    <section className="ludograph-section ludograph-section-last">
      <h6>Most connected</h6>
      <StrengthList indices={props.graph.topList.slice(0, 8)} {...props} />
    </section>
  </>
);

const ClusterView = ({ cluster, onCloseCluster, ...props }) => {
  const { graph } = props;
  const members = [...cluster.members].sort((a, b) => graph.strength[b] - graph.strength[a]);
  return (
    <>
      <section className="ludograph-section">
        <div className="card-kicker">Cluster</div>
        <h2 className="ludograph-title">{cluster.name}</h2>
        <div className="ludograph-body-text">{cluster.size} games</div>
        <div className="ludograph-actions">
          <button className="btn btn-secondary" onClick={onCloseCluster}>
            Close
          </button>
        </div>
      </section>
      <section className="ludograph-section ludograph-section-last">
        <h6>Games in this cluster</h6>
        <StrengthList indices={members} {...props} />
      </section>
    </>
  );
};

const GameView = ({ game, index, graph, games, onSelect, onEnter, onLeave, onCompare, onStartPick, onClose }) => {
  const conns = graph.adj[index];
  return (
    <>
      <section className="ludograph-section">
        <div className="card-kicker">
          {game.year || "—"} · {game.publisher}
        </div>
        <h2 className="ludograph-title">{game.name}</h2>
        <div className="ludograph-body-text">
          {game.designers.length ? game.designers.join(", ") : "Designer unknown"}
        </div>
        {(game.series || game.franchise) && (
          <div className="ludograph-muted ludograph-small">
            {[game.series && `${game.series} series`, game.franchise].filter(Boolean).join(" · ")}
          </div>
        )}
        <div className="ludograph-actions">
          <button className="btn btn-primary ludograph-nowrap" onClick={onStartPick}>
            Compare with…
          </button>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </section>
      <div className="ludograph-stat-pair">
        <div>
          <div className="ludograph-stat-value ludograph-stat-lg">{conns.length}</div>
          <div className="ludograph-stat-label">Links</div>
        </div>
        <div>
          <div className="ludograph-stat-value ludograph-stat-lg">{graph.strength[index].toFixed(1)}</div>
          <div className="ludograph-stat-label">Total weight</div>
        </div>
      </div>
      <section className="ludograph-section ludograph-stack">
        <div>
          <h6>Genres</h6>
          <Tags items={game.genres.map(pretty)} />
        </div>
        <div>
          <h6>Themes</h6>
          <Tags items={game.themes.map(pretty)} />
        </div>
        <div>
          <h6>Mechanics</h6>
          <Tags items={game.mechanics} empty="None listed" />
        </div>
      </section>
      <section className="ludograph-section ludograph-section-last">
        <h6>Strongest connections</h6>
        <Legend />
        {conns.map(({ o, e }) => (
          <div
            key={games[o].id}
            className="ludograph-conn"
            onClick={() => onSelect(o)}
            onMouseEnter={() => onEnter(o)}
            onMouseLeave={onLeave}
          >
            <span className="ludograph-conn-name">{games[o].name}</span>
            <span className="ludograph-num ludograph-conn-pct">{pct(e.s)}%</span>
            <button
              className="btn btn-ghost ludograph-conn-cmp"
              onClick={(ev) => {
                ev.stopPropagation();
                onCompare(o);
              }}
            >
              Compare
            </button>
            <ContributionBar contrib={e.contrib} />
          </div>
        ))}
        {!conns.length && (
          <p className="ludograph-body-text ludograph-muted">
            No links at this threshold. Lower the minimum strength or shift the weighting.
          </p>
        )}
      </section>
    </>
  );
};

const CompareView = ({ model, graph, weights, shares, seriesWeight, sel, cmp, path, onSelect, onSwap, onBack, onClose }) => {
  const { games, pairIndex, n } = model;
  const A = games[sel];
  const B = games[cmp];
  const p = pairIndex[sel * n + cmp];
  const { s, contrib } = scorePair(p, weights, seriesWeight);
  const linked = graph.edges.some((e) => e.a === p.a && e.b === p.b);
  const shared = (a, b) => a.filter((x) => b.includes(x));
  const only = (a, b) => a.filter((x) => !b.includes(x));
  const sharedDesigners = shared(A.designers, B.designers);
  let designerText = "No shared designer or publisher";
  if (sharedDesigners.length) designerText = `Both designed by ${sharedDesigners.join(", ")}`;
  else if (A.publisher === B.publisher) designerText = `Different designers, both published by ${A.publisher}`;
  else if (A.publisherGroup && A.publisherGroup === B.publisherGroup)
    designerText = `${A.publisher} and ${B.publisher} are both part of ${A.publisherGroup}`;

  const rows = [
    {
      label: "Same series",
      sim: p.series ? "Yes" : "No",
      wt: seriesWeight ? `${seriesWeight}% floor` : "Off",
      add: `+${pct(contrib.series)}`,
    },
    ...FACTORS.map((f) => ({
      label: f.label,
      sim: `${pct(p.c[f.key])}%`,
      wt: `${shares[f.key]}%`,
      add: `+${pct(contrib[f.key])}`,
    })),
  ];

  return (
    <>
      <section className="ludograph-section">
        <div className="card-kicker">Comparison</div>
        <div className="ludograph-cmp-names">
          <button className="ludograph-cmp-name ludograph-unbutton" onClick={onBack}>
            {A.name}
          </button>
          <span className="ludograph-muted ludograph-small">and</span>
          <span className="ludograph-cmp-name">{B.name}</span>
        </div>
        <div className="ludograph-big-pct">{pct(s)}%</div>
        <div className="ludograph-cmp-sub">
          link strength · {linked ? "linked directly" : "not linked at current settings"}
        </div>
        <div className="ludograph-actions">
          <button className="btn btn-secondary" onClick={onSwap}>
            Swap
          </button>
          <button className="btn btn-secondary" onClick={onBack}>
            Back
          </button>
          <button className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </section>
      <section className="ludograph-section ludograph-table-section">
        <table className="table">
          <thead>
            <tr>
              <th>Factor</th>
              <th className="ludograph-align-right">Overlap</th>
              <th className="ludograph-align-right">Weight</th>
              <th className="ludograph-align-right">Adds</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td className="ludograph-strong">{r.label}</td>
                <td className="ludograph-align-right ludograph-num">{r.sim}</td>
                <td className="ludograph-align-right ludograph-num">{r.wt}</td>
                <td className="ludograph-align-right ludograph-num ludograph-bold">{r.add}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section className="ludograph-section ludograph-stack">
        <div>
          <h6>Shared themes</h6>
          <Tags items={shared(A.themes, B.themes).map(pretty)} variant="accent" empty="None" />
        </div>
        <div>
          <h6>Shared genres</h6>
          <Tags items={shared(A.genres, B.genres).map(pretty)} variant="accent" empty="None" />
        </div>
        <div>
          <h6>Shared mechanics</h6>
          <Tags items={shared(A.mechanics, B.mechanics)} variant="accent" empty="None" />
        </div>
        <div className="ludograph-two-col">
          <div>
            <div className="ludograph-muted ludograph-small ludograph-label">Only {A.name}</div>
            <Tags items={only(A.mechanics, B.mechanics)} />
          </div>
          <div>
            <div className="ludograph-muted ludograph-small ludograph-label">Only {B.name}</div>
            <Tags items={only(B.mechanics, A.mechanics)} />
          </div>
        </div>
        <div>
          <h6>Designer / publisher</h6>
          <div className="ludograph-body-text">{designerText}</div>
        </div>
      </section>
      <section className="ludograph-section ludograph-section-last">
        <h6>Path through your collection</h6>
        <p className="ludograph-muted ludograph-small">The strongest chain of links between the two games.</p>
        {path ? (
          path.map((i, idx) => {
            const last = idx === path.length - 1;
            const next = last
              ? null
              : scorePair(pairIndex[i * n + path[idx + 1]], weights, seriesWeight).s;
            return (
              <div key={games[i].id} className="ludograph-path-step">
                <div className="ludograph-path-rail">
                  <span
                    className="ludograph-path-dot"
                    style={{
                      background: idx === 0 || last ? "var(--color-accent)" : "var(--color-text)",
                    }}
                  />
                  <span
                    className="ludograph-path-line"
                    style={{ background: last ? "transparent" : "var(--color-accent)" }}
                  />
                </div>
                <button className="ludograph-path-name ludograph-unbutton" onClick={() => onSelect(i)}>
                  {games[i].name}
                </button>
                <span className="ludograph-num ludograph-muted ludograph-small">
                  {last ? "" : `${pct(next)}%`}
                </span>
              </div>
            );
          })
        ) : (
          <p className="ludograph-body-text">No path at this threshold.</p>
        )}
      </section>
    </>
  );
};

const DetailPanel = ({ className, style, ...props }) => {
  const { model, graph, sel, cmp, cluster } = props;
  const activeCluster = sel == null && cluster != null ? graph.clusters[cluster] : null;
  return (
    <aside className={className} style={style}>
      {sel == null && !activeCluster && <EmptyView games={model.games} {...props} />}
      {activeCluster && <ClusterView games={model.games} {...props} cluster={activeCluster} />}
      {sel != null && cmp == null && (
        <GameView game={model.games[sel]} index={sel} games={model.games} {...props} />
      )}
      {sel != null && cmp != null && <CompareView {...props} />}
    </aside>
  );
};

export default DetailPanel;
